import * as React from "react"
import { cn } from "@/lib/utils"

type MediaPlayButtonProps = Omit<
  React.ComponentProps<"button">,
  "children" | "aria-label"
> & {
  "aria-label": string
  /** When true, shows a spinning ring overlay. */
  loading?: boolean
  /** Dim the play/pause icon (e.g. while buffering). */
  dimmed?: boolean
  children: React.ReactNode
}

/**
 * Circular 44×44 play/pause control used by the audio player.
 * `aria-label` is required.
 */
function MediaPlayButton({
  className,
  loading = false,
  dimmed = false,
  children,
  ...props
}: MediaPlayButtonProps) {
  return (
    <button
      type="button"
      data-slot="media-play-button"
      className={cn(
        "relative flex h-[var(--media-play-size)] w-[var(--media-play-size)] shrink-0 cursor-pointer items-center justify-center rounded-full bg-neutral-background-200 transition-transform active:scale-95 disabled:active:scale-100",
        className,
      )}
      {...props}
    >
      {loading ? (
        <div className="pointer-events-none absolute inset-0 animate-spin rounded-full border-2 border-neutral-text-400 border-t-primary-400" />
      ) : null}
      <span className={cn(dimmed && "opacity-50")}>{children}</span>
    </button>
  )
}

type RecordButtonProps = Omit<React.ComponentProps<"button">, "children"> & {
  children: React.ReactNode
}

/**
 * Full-width pill recording CTA. Callers own icon, label, and waveform.
 */
const RecordButton = React.forwardRef<HTMLButtonElement, RecordButtonProps>(
  function RecordButton({ className, children, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        data-slot="record-button"
        className={cn(
          "group relative flex h-[var(--record-button-height)] w-full cursor-pointer items-center overflow-hidden rounded-full transition-all duration-300 select-none",
          "border bg-neutral-background-100",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
          "aria-invalid:border-state-negative/25 aria-invalid:bg-state-negative-light",
          "aria-pressed:border-state-negative/25 aria-pressed:ring-1 aria-pressed:ring-state-negative/10",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)

export { MediaPlayButton, RecordButton }
